import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import SuperLayoutTitle from "@/components/layout/super-layout/super-layout-title";
import { Doc, Id } from "@/convex/_generated/dataModel";
import { api } from "@/convex/_generated/api";
import { createTableHook, TableProps } from "@/hooks/useTableFactory";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import CompanyModal from "@/components/companies/company-modal";
import { useToastMutation } from "@/hooks/useToastMutation";
import { CreditCard, Shield, UserPlus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useModal } from "@/hooks/useModal";
import CompanyUsersAssignmentWizard from "@/components/users/company-users-assignment-wizard";

const companiesSearchSchema = z.object({
  page: z.number().optional().default(1),
  search: z.string().optional(),
  status: z.string().optional(),
});

export const Route = createFileRoute("/_super/super/companies")({
  validateSearch: companiesSearchSchema,
  component: CompaniesPage,
});

type CompanyData = Doc<"companies">;

const useCompaniesTable = createTableHook<CompanyData>({
  tableName: "companies",
  query: api.companies.queries.getCompaniesOptimized,
  title: "Empresas",
  description: "Lista de empresas del sistema",
  createAction: "modal",

  columns: () => [
    {
      sortable: true,
      key: "name",
      label: "Nombre",
      required: true,
      render: (value) => <div className="font-medium">{value}</div>,
    },
    {
      sortable: true,
      key: "taxId",
      label: "RTN",
      render: (value) => (
        <div className="text-sm text-muted-foreground">{value || "-"}</div>
      ),
    },
    {
      sortable: true,
      key: "email",
      label: "Email",
      render: (value) => (
        <div className="text-sm text-muted-foreground">{value || "-"}</div>
      ),
    },
    {
      sortable: true,
      key: "phone",
      label: "Teléfono",
      render: (value) => (
        <div className="text-sm text-muted-foreground">{value || "-"}</div>
      ),
    },
    {
      sortable: true,
      key: "status",
      label: "Estado",
      render: (value: string) => {
        const variants: Record<
          string,
          "default" | "secondary" | "destructive"
        > = {
          active: "default",
          inactive: "secondary",
          suspended: "destructive",
        };
        const labels: Record<string, string> = {
          active: "Activo",
          inactive: "Inactivo",
          suspended: "Suspendido",
        };
        return (
          <Badge variant={variants[value] || "secondary"}>
            {labels[value] || value}
          </Badge>
        );
      },
    },
    {
      sortable: true,
      key: "_creationTime",
      label: "Creado",
      render: (value: number) => {
        const date = new Date(value);
        return (
          <div className="text-sm text-muted-foreground">
            {format(date, "dd MMM yyyy", { locale: es })}
          </div>
        );
      },
    },
  ],

  filters: () => [
    {
      id: "status",
      name: "Estado",
      icon: "Activity",
      options: [
        { value: "all", label: "Todos" },
        { value: "active", label: "Activos" },
        { value: "inactive", label: "Inactivos" },
        { value: "suspended", label: "Suspendidos" },
      ],
      defaultValue: "all",
    },
  ],

  customHooks: () => {
    const [isActionLoading, setIsActionLoading] = useState(false);
    const assignUsersModal = useModal<{
      companyId: Id<"companies">;
      companyName: string;
    }>();

    const { execute: verifyPlan } = useToastMutation(
      api.companies.subscriptions.mutations.verifyOrCreateCompanySubscription,
      {
        loadingMessage: "Verificando plan...",
        successMessage: "Plan verificado exitosamente",
      },
    );

    const { execute: createPermissions } = useToastMutation(
      api.system.permissions.mutations.createDefaultCompanyPermissions,
      {
        loadingMessage: "Creando permisos...",
        successMessage: "Permisos creados exitosamente",
      },
    );

    const handleVerifyPlan = async (companyId: Id<"companies">) => {
      setIsActionLoading(true);
      try {
        const result = await verifyPlan({ companyId });
        if (result) {
          toast.success(`Acción: ${result.action}`, {
            description: result.message,
          });
        }
      } finally {
        setIsActionLoading(false);
      }
    };

    const handleCreatePermissions = async (companyId: Id<"companies">) => {
      setIsActionLoading(true);
      try {
        const result = await createPermissions({ companyId });
        if (result) {
          toast.success(`${result.total} permisos creados`);
        }
      } finally {
        setIsActionLoading(false);
      }
    };

    return {
      isActionLoading,
      handleVerifyPlan,
      handleCreatePermissions,
      assignUsersModal,
    };
  },

  actions: (helpers, customHooksData: Record<string, any>) => {
    const {
      handleVerifyPlan,
      handleCreatePermissions,
      isActionLoading,
      assignUsersModal,
    } = customHooksData;

    const { execute: deleteCompany } = useToastMutation(
      api.companies.mutations.deleteCompany,
    );

    return {
      onEdit: (company) => {
        helpers.modal.edit(company);
      },
      onDelete: async (company) => {
        await deleteCompany({ id: company._id as Id<"companies"> });
      },
      customActions: [
        {
          label: "Asignar Usuarios",
          icon: UserPlus,
          onClick: (company: CompanyData) =>
            assignUsersModal.open({
              companyId: company._id,
              companyName: company.name,
            }),
          variant: "default" as const,
        },
        {
          label: "Verificar Plan",
          icon: CreditCard,
          onClick: (company: CompanyData) => handleVerifyPlan(company._id),
          disabled: isActionLoading,
        },
        {
          label: "Crear Permisos",
          icon: Shield,
          onClick: (company: CompanyData) =>
            handleCreatePermissions(company._id),
          disabled: isActionLoading,
        },
      ],
    };
  },

  modal: {
    component: CompanyModal,
    getItemName: (company) => company?.name || "Empresa",
  },
});

function CompaniesPage() {
  const searchParams = Route.useSearch();

  return (
    <>
      <SuperLayoutTitle
        title="Gestión de Empresas"
        subtitle="Administra todas las empresas del sistema"
      />
      <CompaniesTable
        currentPage={searchParams.page}
        pageSize={20}
        basePath="/super/companies"
        searchParams={searchParams}
      />
    </>
  );
}

export default function CompaniesTable({
  currentPage,
  pageSize,
  basePath,
  searchParams,
}: TableProps) {
  const table = useCompaniesTable({
    currentPage,
    pageSize,
    basePath,
    searchParams,
  });

  const { assignUsersModal } = table.customHooksData as any;

  return (
    <>
      {table.render()}

      {assignUsersModal.data && (
        <CompanyUsersAssignmentWizard
          isOpen={assignUsersModal.isOpen}
          onClose={assignUsersModal.close}
          companyId={assignUsersModal.data.companyId}
          companyName={assignUsersModal.data.companyName}
          onSuccess={() => {
            assignUsersModal.close();
          }}
        />
      )}
    </>
  );
}
